import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { 
  ChevronLeft, 
  Upload, 
  FileText, 
  Trash2, 
  Edit2, 
  Download,
  Loader2,
  Plus,
  X,
  Eye,
  EyeOff,
  FolderOpen,
  FileArchive
} from 'lucide-react'
import { toast } from 'sonner'

interface Document {
  id: string
  title: string
  description: string | null
  filename: string
  originalName: string
  fileSize: number
  mimeType: string
  category: string
  isPublished: boolean
  order: number
  createdAt: string
}

// TODAS AS CATEGORIAS
const categoryOptions = [
  { value: 'estudos', label: 'Estudos Técnicos de Viabilidade Econômica, Financeira e Jurídica' },
  { value: 'minutas', label: 'Minutas e Regulamentos' },
  { value: 'consultas', label: 'Consultas e Audiências Públicas' },
  { value: 'atas', label: 'Atas do CGPPP' },
  { value: 'edital', label: 'Minuta do Edital e Anexos' },
  { value: 'contrato', label: 'Minuta do Contrato e Anexos' },
  { value: 'galeria', label: 'Galeria do Projeto' }
  
]

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function DocumentsManager() {
  const { token } = useAuth()
  const [documents, setDocuments] = useState<Document[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [editingDoc, setEditingDoc] = useState<Document | null>(null)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'estudos',
    order: 0,
    file: null as File | null
  })

  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    category: 'estudos',
    order: 0,
    isPublished: true
  })

  useEffect(() => {
    loadDocuments()
  }, [token])

  const loadDocuments = async () => {
    if (!token) {
      console.warn('Token não disponível')
      setIsLoading(false)
      return
    }
    
    setIsLoading(true)
    try {
      const data = await api.getAdminDocuments(token)
      setDocuments(data)
    } catch (error) {
      console.error('Erro ao carregar documentos:', error)
      toast.error('Erro ao carregar documentos')
    } finally {
      setIsLoading(false)
    }
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('zip')) return <FileArchive size={18} className="text-[#51A64D]" />
    return <FileText size={18} className="text-[#51A64D]" />
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.includes('pdf') && 
          !file.type.includes('msword') && 
          !file.type.includes('openxmlformats') &&
          !file.type.includes('zip')) {
        toast.error('Apenas arquivos PDF, DOC, DOCX ou ZIP são permitidos')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo 10MB')
        return
      }
      setUploadForm(prev => ({ ...prev, file }))
    }
  }

  const handleUpload = async () => {
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.')
      return
    }
    
    if (!uploadForm.title.trim()) {
      toast.error('Título é obrigatório')
      return
    }
    if (!uploadForm.file) {
      toast.error('Selecione um arquivo')
      return
    }

    setUploading(true)
    try {
      await api.uploadDocument(token, {
        title: uploadForm.title,
        description: uploadForm.description,
        category: uploadForm.category,
        order: uploadForm.order,
        file: uploadForm.file
      })
      toast.success('Documento enviado com sucesso!')
      setShowUploadModal(false)
      setUploadForm({
        title: '',
        description: '',
        category: 'estudos',
        order: 0,
        file: null
      })
      if (fileInputRef.current) fileInputRef.current.value = ''
      await loadDocuments()
    } catch (error: any) {
      toast.error(error.message || 'Erro ao enviar documento')
    } finally {
      setUploading(false)
    }
  }

  const handleEdit = async () => {
    if (!token || !editingDoc) {
      toast.error('Erro ao atualizar documento')
      return
    }
    
    if (!editForm.title.trim()) {
      toast.error('Título é obrigatório')
      return
    }

    setUploading(true)
    try {
      await api.updateDocument(token, editingDoc.id, {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        order: editForm.order,
        isPublished: editForm.isPublished
      })
      toast.success('Documento atualizado com sucesso!')
      setEditingDoc(null)
      await loadDocuments()
    } catch (error: any) {
      toast.error(error.message || 'Erro ao atualizar documento')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (doc: Document) => {
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.')
      return
    }
    
    if (!confirm(`Tem certeza que deseja excluir "${doc.title}"?`)) return
    
    setDeletingId(doc.id)
    try {
      await api.deleteDocument(token, doc.id)
      toast.success('Documento removido com sucesso!')
      await loadDocuments()
    } catch (error: any) {
      toast.error(error.message || 'Erro ao remover documento')
    } finally {
      setDeletingId(null)
    }
  }

  const handleDownload = async (doc: Document) => {
    try {
      await api.downloadDocument(doc.id, doc.originalName)
      toast.success(`Download iniciado: ${doc.title}`)
    } catch (error) {
      toast.error('Erro ao baixar o arquivo')
    }
  }

  const openEditModal = (doc: Document) => {
    setEditingDoc(doc)
    setEditForm({
      title: doc.title,
      description: doc.description || '',
      category: doc.category,
      order: doc.order,
      isPublished: doc.isPublished
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#51A64D] mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando documentos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
              <ChevronLeft size={20} />
            </a>
            <div>
              <h1 className="text-xl font-serif font-bold text-gray-900">Gerenciar Documentos</h1>
              <p className="text-sm text-gray-500">Faça upload e gerencie os documentos do projeto</p>
            </div>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white text-sm rounded-lg hover:bg-[#458f3f] transition-colors"
          >
            <Upload size={16} />
            Novo Documento
          </button>
        </div>
      </div>

      <div className="p-6">
        {documents.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
            <FolderOpen size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">Nenhum documento cadastrado</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white text-sm rounded-lg hover:bg-[#458f3f] transition-colors"
            >
              <Plus size={16} />
              Adicionar primeiro documento
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Documento</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Categoria</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Data</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Tamanho</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {getFileIcon(doc.mimeType)}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                            {doc.description && (
                              <div className="text-xs text-gray-400 mt-0.5">{doc.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs text-gray-500">
                          {categoryOptions.find(c => c.value === doc.category)?.label || doc.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(doc.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatFileSize(doc.fileSize)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          doc.isPublished 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {doc.isPublished ? (
                            <>
                              <Eye size={10} />
                              Publicado
                            </>
                          ) : (
                            <>
                              <EyeOff size={10} />
                              Oculto
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownload(doc)}
                            className="p-1.5 text-gray-400 hover:text-[#51A64D] transition-colors"
                            title="Baixar"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            onClick={() => openEditModal(doc)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(doc)}
                            disabled={deletingId === doc.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            title="Excluir"
                          >
                            {deletingId === doc.id ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Trash2 size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Upload */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-serif font-bold text-lg text-gray-900">Upload de Documento</h3>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                  placeholder="Ex: Edital de Licitação nº 001/2026"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Descrição (opcional)
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20 min-h-[80px]"
                  placeholder="Breve descrição do documento..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Categoria
                </label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                >
                  {categoryOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Ordem
                </label>
                <input
                  type="number"
                  value={uploadForm.order}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                  placeholder="0"
                />
                <p className="text-xs text-gray-400 mt-1">Número menor = aparece primeiro</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Arquivo <span className="text-red-500">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.zip"
                  onChange={handleFileChange}
                  className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#51A64D] file:text-white hover:file:bg-[#458f3f] transition-colors"
                />
                <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX ou ZIP até 10MB</p>
              </div>
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white text-sm rounded-lg hover:bg-[#458f3f] transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Enviar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {editingDoc && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-serif font-bold text-lg text-gray-900">Editar Documento</h3>
              <button onClick={() => setEditingDoc(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Descrição
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20 min-h-[80px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Categoria
                </label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                >
                  {categoryOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Ordem
                </label>
                <input
                  type="number"
                  value={editForm.order}
                  onChange={(e) => setEditForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.isPublished}
                    onChange={(e) => setEditForm(prev => ({ ...prev, isPublished: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-[#51A64D] focus:ring-[#51A64D]"
                  />
                  <span className="text-sm text-gray-700">Documento publicado (visível no site)</span>
                </label>
              </div>
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setEditingDoc(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleEdit}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white text-sm rounded-lg hover:bg-[#458f3f] transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}